import React, { Component, ReactNode } from "react";
import "react-toastify/dist/ReactToastify.css";
import { ErrorNotificationContext } from "./contexts/ErrorNotificationContext";
// Class component, so no useTranslation hook. i18next's singleton falls back to
// the key (the English string) if this renders before init - which, on the
// last-resort error screen, is exactly the behaviour we want.
import { translate } from './i18n/translate';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
  errorCount: number;
  firstErrorAt: number | null;
  lastErrorMessage: string | null;
}

// If an error occurs >= MAX_ERRORS_PER_WINDOW times within WINDOW_MS,
// stop recovering and render a fallback. Without this, a component that throws
// on every render (e.g. provider that fails on mount) creates an infinite
// catch -> reset -> re-render -> throw loop that also floods telemetry.
const MAX_ERRORS_PER_WINDOW = 3;
const WINDOW_MS = 5000;

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static contextType = ErrorNotificationContext;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: null, errorCount: 0, firstErrorAt: null, lastErrorMessage: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught in ErrorBoundary:", error, errorInfo);

    const context = this.context as any;
    if (context && context.addError) {
      context.addError(
        error.message || 'An unexpected error occurred',
        `boundary-${this.state.errorCount + 1}`
      );
    }

    const now = Date.now();
    const { errorCount, firstErrorAt, lastErrorMessage } = this.state;
    const withinWindow = firstErrorAt !== null && now - firstErrorAt < WINDOW_MS;
    const nextCount = withinWindow ? errorCount + 1 : 1;
    const nextFirst = withinWindow ? firstErrorAt : now;

    // The SAME error repeating means auto-recovery just re-mounts the children,
    // which re-triggers the exact same throw (e.g. re-opening a corrupt file) —
    // an infinite catch -> reset -> throw loop that does NOT depend on timing, so
    // the time window alone never stops it. Latch as soon as an identical error
    // repeats (one retry at most). A one-off / different error still recovers.
    const sameAsLast = !!error.message && error.message === lastErrorMessage;
    const shouldLatch = sameAsLast || nextCount >= MAX_ERRORS_PER_WINDOW;

    this.setState({
      hasError: shouldLatch,
      errorCount: nextCount,
      firstErrorAt: nextFirst,
      lastErrorMessage: error.message || null,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, errorMessage: null, errorCount: 0, firstErrorAt: null, lastErrorMessage: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, fontFamily: 'sans-serif', maxWidth: 640, margin: '40px auto' }}>
          <h2 style={{ marginTop: 0 }}>{translate('Something went wrong')}</h2>
          <p>{translate('The application encountered repeated errors and stopped reloading the affected view to prevent further issues.')}</p>
          {this.state.errorMessage && (
            <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 12, overflow: 'auto' }}>
              {this.state.errorMessage}
            </pre>
          )}
          <button
            onClick={this.handleReset}
            style={{ padding: '8px 16px', borderRadius: 4, border: '1px solid #ccc', cursor: 'pointer', marginRight: 8 }}
          >
            {translate('Try again')}
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '8px 16px', borderRadius: 4, border: '1px solid #ccc', cursor: 'pointer' }}
          >
            {translate('Reload page')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
